import './ClusterEnableIncortaXEdit.less';

import React from 'react';
import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import { Modal, notification, Switch, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useMutateIncortaXState } from 'hooks/cluster';
import { ReactComponent as BiTools } from 'images/bi-tools-icon.svg';
import { Instance, ServerConfiguration, Service } from 'types/cluster';
import CopyableText from 'components/CopyableText/CopyableText';
import { getClusterStatus } from 'utils/cluster';
import { UserData } from 'types/user';
import useToggleFeature, { TOGGLE_FEATURE_KEYS } from 'hooks/useToggleFeature';

interface ClusterEnableIncortaXEditProps {
  instance: Instance;
  incortaXServerConfiguration: ServerConfiguration[];
  services: Service[];
  user: UserData;
}

function ClusterEnableIncortaXEdit({
  instance,
  incortaXServerConfiguration,
  services,
  user,
}: ClusterEnableIncortaXEditProps) {
  let { mutateAsync: mutateIncortaXState, isLoading } = useMutateIncortaXState({
    clusterName: instance.name,
  });

  const { isToggleFeatureEnabled: isIncortaXFeatureEnabled } = useToggleFeature(
    {
      user,
      featureKey: TOGGLE_FEATURE_KEYS.incortaX,
    },
  );

  async function handleChange(enable: boolean) {
    if (enable) {
      Modal.confirm({
        title: 'Enable Advanced SQL Interface',
        content:
          'Enabling Advanced SQL Interface will restart Incorta Services, are you sure you want to proceed?',
        okText: 'Proceed',
        centered: true,
        width: 620,
        async onOk() {
          let { data } = await mutateIncortaXState(true);
          notification.success({
            message: 'Success',
            description: data.message,
          });
        },
      });
    } else {
      Modal.confirm({
        title: 'Disable Advanced SQL Interface',
        content:
          'Disabling will lead to loss of any current or upcoming query requests.',
        okText: 'Disconnect',
        centered: true,
        width: 620,
        async onOk() {
          let { data } = await mutateIncortaXState(false);
          notification.success({
            message: 'Success',
            description: data.message,
          });
        },
      });
    }
  }

  let canUpdate = instance.clusterPolicy.clusterPolicy.includes('update');
  let status = getClusterStatus(instance, services[0]);

  let enable = status === 'running';
  return isIncortaXFeatureEnabled ? (
    <div className="ClusterEdit ClusterEnableIncortaXEdit">
      <div className="ClusterEdit__icon">
        <Icon component={BiTools} />
      </div>
      <div className="ClusterEdit__content ClusterEnableIncortaXEdit__content">
        <div className="ClusterEnableIncortaXEdit__main">
          <h3 className="ClusterEdit__title">Enable Advanced SQL Interface</h3>
          <div className="ClusterEnableIncortaXEdit__body">
            <p className="ClusterEnableIncortaXEdit__hint">
              <FormattedMessage id="clusterConfiguration.incortaX.mainHint" />
              <label className="ClusterEnableIncortaXEdit__hint-more">
                <FormattedMessage id="clusterConfiguration.incortaX.moreHint" />
              </label>
            </p>

            {instance.incortaXEnabled ? (
              <div className="ClusterEnableIncortaXEdit__info-wrapper">
                {incortaXServerConfiguration.map(config => {
                  return (
                    <div
                      key={config.name}
                      className="ClusterEnableIncortaXEdit__info-box"
                    >
                      <div className="ClusterEnableIncortaXEdit__info-box-label">
                        {config.name}{' '}
                        <Tooltip
                          placement="top"
                          title={config.description}
                          overlayClassName="full-width-tooltip"
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                      <div className="ClusterEnableIncortaXEdit__info-box-content">
                        <CopyableText text={config.text} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
        <div className="ClusterEnableIncortaXEdit__switch-button">
          {canUpdate && isIncortaXFeatureEnabled && (
            <Tooltip
              title={
                status !== 'running'
                  ? 'Your cluster must be connected to apply this change'
                  : instance.platform !== 'gc'
                  ? 'Advanced SQL Interface is Currently Unavailable for Azure Clusters!'
                  : !instance.isIncortaXEligible
                  ? 'Unlock Advanced SQL Interface: Upgrade your cluster today for access. Enhance your data capabilities now!'
                  : ''
              }
            >
              <Switch
                onChange={handleChange}
                loading={isLoading}
                checked={instance.incortaXEnabled}
                disabled={
                  !enable ||
                  !isIncortaXFeatureEnabled ||
                  !instance.isIncortaXEligible ||
                  instance.platform !== 'gc'
                }
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  ) : null;
}

export default ClusterEnableIncortaXEdit;
